import { useState } from "react";
import { useParams } from "react-router-dom";
import { useAtomValue } from "jotai";

import RichTextEditorV2 from "../../CRM/RichTextEditorV2";
import IconButton from "../../../elements/IconButton";
import useUpdateRecord from "../../../api/api-hooks/records/useUpdateRecord";
import StrUtils from "../../../utils/StrUtils";
import { newToastEvent } from "../../../utils/ToastUtil";
import usePage from "../../../api/api-hooks/pages/usePage";
import EditSaveButton from "../../../elements/EditSaveButton";
import { currentViewAtom } from "../../../atoms/ViewAtoms";

interface Props {
  recordID: any;
  field: any;
  savedRichText?: string;
  moduleAPIName: any;
  title?: string;
  description?: string;
  editDisabled?: boolean;
  headerOptions?: React.ReactNode;
}

const RichTextWidget = ({
  recordID,
  field,
  savedRichText,
  moduleAPIName,
  title,
  description,
  headerOptions,
}: Props) => {
  const { pageID, workspaceID }: any = useParams<{
    pageID: string;
    workspaceID: string;
  }>();
  const { page } = usePage(pageID);
  const view = useAtomValue(currentViewAtom);

  const [editingText, setEditingText] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [richText, setRichText] = useState(savedRichText || "");

  const { updateRecord, recordUpdating, updateRecordError } = useUpdateRecord(
    false,
    recordID
  );

  const handleSave = () => {
    if (savedRichText === richText) return setEditingText(false);
    setSubmitting(true);

    const editedRecordInPageActivity = {
      id: StrUtils.uuidv4(),
      Type: "edited-record-in-page",
      Page: {
        id: page?.id,
        Name: page?.Name,
        Logo: page?.Icon,
      },
      View: {
        id: view?.id,
        Name: view?.Name,
        Type: view?.Type,
      },
    };

    updateRecord({
      field,
      recordID,
      payload: {
        [field.APIName]: {
          RichText: richText ? richText : "",
          PlainText: richText
            ? StrUtils.convertMarkupToPlaintext(richText)
            : "",
        },
      },
      moduleAPIName: moduleAPIName,
      _UserActivity: {
        WorkspaceID: workspaceID,
        Activity: editedRecordInPageActivity,
      },
      originalRecord: undefined,
      onSuccess: () => {
        setEditingText(false);
        setSubmitting(false);

        newToastEvent("success", `Successfully updated ${field.Name}.`);
      },
    });
  };

  return (
    <div className="d-flex flex-column mb-3">
      <div className="d-flex align-items-center mb-2 flex-wrap justify-content-between">
        <div className="d-flex align-items-center mb-1">
          {title && (
            <div>
              <h3 className="mb-0">{title}</h3>
              <small>{description}</small>
            </div>
          )}
        </div>
        <div className="d-flex flex-wrap justify-content-start align-items-center">
          {headerOptions}
          {!field?.ReadOnly && (
            <>
              {editingText ? (
                <EditSaveButton
                  isEditing={editingText}
                  setIsEditing={setEditingText}
                  onCancel={() => setRichText(savedRichText || "")}
                  onSave={handleSave}
                  isSubmitting={submitting}
                />
              ) : (
                <IconButton
                  icon="pencil"
                  onClick={() => setEditingText(true)}
                />
              )}
            </>
          )}
        </div>
      </div>
      <RichTextEditorV2
        isEditing={editingText && !submitting}
        value={richText}
        onChange={(val: any) => setRichText(val)}
        toggleEdit={() => setEditingText(true)}
        editDisabled={!!field?.ReadOnly}
      />
    </div>
  );
};

export default RichTextWidget;
